import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';

import * as fromStore from '../../../store';

import { odataParamsFactory, toastFactory } from '@tdw/core';
import { withLatestFrom, switchMap, map, catchError, of, filter, tap } from 'rxjs';
import { closuresAdminFilterFactory } from 'src/app/models';
import { ClosureService } from 'src/app/services/closure.service';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';

@Injectable()
export class ClosuresAdminEffects {

  loadClosures$ = createEffect(() => this.actions$.pipe(
    ofType(
      fromStore.actions.ADMIN.loadClosures,
      fromStore.actions.ADMIN.loadClosuresForPage,
      fromStore.actions.ADMIN.updateClosuresAdminFilter,
      fromStore.actions.ADMIN.saveClosureRecordSuccess),
    withLatestFrom(
      this.store.select(fromStore.selectors.ADMIN.selectClosureQuery),
      this.store.select(fromStore.selectors.ADMIN.selectClosureAdminFilter)),
    switchMap(([action, q, adminFilter]) => this.closureService.getClosureMaintenance(odataParamsFactory({
      ...q,
      filter: closuresAdminFilterFactory(adminFilter).toOdataFilter()
    }))
      .pipe(map(closures => fromStore.actions.ADMIN.loadClosuresSuccess({ closures })),
        catchError(error => of(fromStore.actions.ADMIN.loadClosuresFail({ error })))))));

  loadCurrentlyEditingClosure$ = createEffect(() => this.actions$.pipe(
    ofType(
      fromStore.actions.ADMIN.loadCurrentlyEditingClosure,
      fromStore.actions.ADMIN.addClosureDesignCodeSuccess,
      fromStore.actions.ADMIN.removeClosureDesignCodeSuccess,
      fromStore.actions.ADMIN.addClosureDesignPressureSuccess,
      fromStore.actions.ADMIN.removeClosureDesignPressureSuccess,
      // fromStore.actions.ADMIN.addAssociatedProductSuccessful,
      // fromStore.actions.ADMIN.removeAssociatedProductSuccess,
      // fromStore.actions.ADMIN.uploadDocumentSuccess
    ),
    withLatestFrom(this.store.select(fromStore.selectors.ADMIN.selectCurrentlyEditingClosure)),
    map(([action, currentlyEditingClosure]) => {
      if (action.type === fromStore.actions.ADMIN.loadCurrentlyEditingClosure.type) return action.closureId;
      return currentlyEditingClosure?.id;
    }),
    filter(f => !!f),
    switchMap(id => this.closureService.getClosureById(id || -1)
      .pipe(map(closure => fromStore.actions.ADMIN.loadCurrentlyEditingClosureSuccess({ closure })),
        catchError(error => of(fromStore.actions.ADMIN.loadCurrentlyEditingClosureFail({ error })))))));

  saveClosure$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.saveClosureRecord),
    switchMap(action => this.closureService.saveClosure(action.closureProduct)
      .pipe(map(() => fromStore.actions.ADMIN.saveClosureRecordSuccess()),
        catchError(error => of(fromStore.actions.ADMIN.saveClosureRecordFail({ error }))))
    )
  ));

  showMessageAfterClosureSave$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.saveClosureRecordSuccess),
    map(action => fromStore.actions.MESSAGES.addToast({
      toast: toastFactory({
        class: 'success',
        message: 'The Closure record has been saved',
        title: 'Closure'
      })
    }))));

  navAfterSave$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.saveClosureRecordSuccess),
    tap(action => this.router.navigateByUrl('/admin'))
  ), { dispatch: false });

  addDesignCode$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.addClosureDesignCode),
    switchMap(action => this.closureService.addDesignCode({ closureProductId: action.closureId, designCodeId: action.designCodeId })
      .pipe(map(closureDetail => fromStore.actions.ADMIN.addClosureDesignCodeSuccess({ closureDetail })),
        catchError(error => of(fromStore.actions.ADMIN.addClosureDesignCodeFail({ error }))))
    )
  ));

  showToastForDesignCodeAdd$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.addClosureDesignCodeSuccess),
    map(action => fromStore.actions.MESSAGES.addToast({
      toast: toastFactory({
        class: 'success',
        message: 'Design code was added successfully',
        title: 'Closure'
      })
    }))
  ));

  removeDesigCode$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.removeClosureDesignCode),
    switchMap(action => this.closureService.removeDesignCode(action.closureProductDesignCodeId)
      .pipe(map(() => fromStore.actions.ADMIN.removeClosureDesignCodeSuccess()),
        catchError(error => of(fromStore.actions.ADMIN.removeClosureDesignCodeFail({ error })))))));

  showToastForDesignCodeRemove$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.removeClosureDesignCodeSuccess),
    map(action => fromStore.actions.MESSAGES.addToast({
      toast: toastFactory({
        class: 'success',
        message: 'Design code was removed successfully',
        title: 'Closure'
      })
    }))));

  addDesignPressure$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.addClosureDesignPressure),
    switchMap(action => this.closureService.addDesignPressure({ designFactorId: action.designFactorId, closureProductId: action.closureId, maxDesignPressure: action.maxPressure })
      .pipe(map(() => fromStore.actions.ADMIN.addClosureDesignPressureSuccess()),
        catchError(error => of(fromStore.actions.ADMIN.addClosureDesignPressureFail({ error })))))));

  removeDesignPressure$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.removeClosureDesignPressure),
    switchMap(action => this.closureService.removeDesignPressure(action.closureProductDesignPressureId)
      .pipe(map(() => fromStore.actions.ADMIN.removeClosureDesignPressureSuccess()),
        catchError(error => of(fromStore.actions.ADMIN.removeClosureDesignPressureFail({ error })))))));

  showToastForDesignPressureRemove$ = createEffect(() => this.actions$.pipe(
    ofType(fromStore.actions.ADMIN.removeClosureDesignPressureSuccess),
    map(action => fromStore.actions.MESSAGES.addToast({
      toast: toastFactory({
        class: 'success',
        message: 'Design pressure was removed successfully',
        title: 'Closure'
      })
    }))));

  constructor(
    private actions$: Actions,
    private closureService: ClosureService,
    private store: Store<fromStore.State>,
    private router: Router) { }
}
